import { useCallback, useRef } from 'react'

import { useNavigate } from 'react-router-dom'

import { F0WizardForm } from '@factorialco/f0-react'
import type { F0FormSubmitResult, F0WizardFormPerSectionSubmitArg } from '@factorialco/f0-react'
import { HasEmployeesToCreateRequestsDocument } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import { track } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { sumMinutesToHours } from 'modules/trainings/components/MyTrainings/RequestsComponent/RequestDetailComponent/useHoursAndMinutes'
import { useCreateTrainingRequest } from 'modules/trainings/hooks/TrainingRequests/useCreateTrainingRequest'
import { trainingEvents } from 'modules/trainings/lib/events'

import { useNewProposalForm } from './form'
import type { NewProposalSchema, NewProposalFormValues } from './form'

export interface Props {
  isOpen?: boolean
  onClose?: () => void
  back?: string
}

const formatDate = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const Wizard = ({ isOpen = true, onClose, back }: Props) => {
  const navigate = useNavigate()
  const { access, company, employee } = useCurrentSession()
  const { create } = useCreateTrainingRequest()
  const targetEmployeeId = employee?.id
  const latestFullDataRef = useRef<Partial<NewProposalFormValues> | null>(null)

  const { data: employeesData, loading } = useQuery(HasEmployeesToCreateRequestsDocument, {
    fetchPolicy: 'network-only',
  })

  const showParticipantsStep =
    Number(employeesData?.employees.employeeNamesConnection?.totalCount ?? 0) > 0

  const amplitudeEvents =
    trainingEvents.trainingRequest.addTrainingRequest.newProposal

  const closeWizard = useCallback(() => {
    if (onClose) {
      onClose()
    } else if (back) {
      navigate(back)
    }
  }, [navigate, back, onClose])

  const handleSubmit = useCallback(
    async (
      arg: F0WizardFormPerSectionSubmitArg<NewProposalSchema>
    ): Promise<F0FormSubmitResult> => {
      // Capture latest form data for draft saving on close
      latestFullDataRef.current = arg.fullData

      // Per-section mode: only fire mutation on the last section (comments)
      if (arg.sectionId !== 'comments') {
        return { success: true }
      }

      const { trainingNeed } = arg.fullData.trainingNeed
      const { trainingName, providerName, externalReference, competencyIds } =
        arg.fullData.basicInfo
      const { dateRange, duration, minutes, schedule, cost, subsidized, paymentInstallments } =
        arg.fullData.timingCost
      const { employeesSelection } = arg.fullData.employees
      const { comments } = arg.data

      const result = await create({
        authorId: access.id,
        companyId: String(company.id),
        status: 'review',
        subsidized: subsidized ?? false,
        competencyIds: competencyIds ?? [],
        employeesSelection: employeesSelection ?? {
          type: 'manual',
          ids: targetEmployeeId ? [targetEmployeeId] : [],
        },
        trainingName,
        trainingNeed,
        providerName,
        externalReference,
        comments,
        duration: sumMinutesToHours(duration, minutes),
        schedule,
        cost,
        startDate: dateRange?.from ? formatDate(dateRange.from) : undefined,
        endDate: dateRange?.to ? formatDate(dateRange.to) : undefined,
        paymentInstallments: paymentInstallments ? Number(paymentInstallments) : undefined,
      })

      let formErrors: Record<string, string> = {}
      const handled = handleResult(result, (errors) => {
        formErrors = errors
      })

      if (!handled.success) {
        return { success: false, errors: formErrors }
      }

      track(amplitudeEvents.submit, { from: 'my_request' })

      app.setFlash({
        type: 'correct',
        message: i18n.t('trainings.training_requests.create.success_messages.pending'),
      })

      latestFullDataRef.current = null
      closeWizard()
      return { success: true }
    },
    [
      create,
      access.id,
      company.id,
      targetEmployeeId,
      amplitudeEvents.submit,
      closeWizard,
    ],
  )

  const handleClose = useCallback(async () => {
    const fullData = latestFullDataRef.current
    if (fullData) {
      const result = await create({
        authorId: access.id,
        companyId: String(company.id),
        status: 'draft',
        subsidized: fullData.timingCost?.subsidized ?? false,
        competencyIds: fullData.basicInfo?.competencyIds ?? [],
        employeesSelection: fullData.employees?.employeesSelection ?? {
          type: 'manual',
          ids: targetEmployeeId ? [targetEmployeeId] : [],
        },
        trainingName: fullData.basicInfo?.trainingName ?? '',
        trainingNeed: fullData.trainingNeed?.trainingNeed,
        providerName: fullData.basicInfo?.providerName,
        externalReference: fullData.basicInfo?.externalReference,
        comments: fullData.comments?.comments,
        duration: sumMinutesToHours(
          fullData.timingCost?.duration,
          fullData.timingCost?.minutes,
        ),
        schedule: fullData.timingCost?.schedule,
        cost: fullData.timingCost?.cost,
        startDate: fullData.timingCost?.dateRange?.from
          ? formatDate(fullData.timingCost.dateRange.from)
          : undefined,
        endDate: fullData.timingCost?.dateRange?.to
          ? formatDate(fullData.timingCost.dateRange.to)
          : undefined,
        paymentInstallments: fullData.timingCost?.paymentInstallments
          ? Number(fullData.timingCost.paymentInstallments)
          : undefined,
      })

      const handled = handleResult(result, () => undefined)
      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t(
            'trainings.training_requests.create.success_messages.draft',
          ),
        })
      }
    }

    track(amplitudeEvents.cancel, { from: 'my_request' })
    closeWizard()
  }, [
    create,
    access.id,
    company.id,
    targetEmployeeId,
    amplitudeEvents.cancel,
    closeWizard,
  ])

  const handleStepChanged = useCallback(() => {
    track(amplitudeEvents.next, { from: 'my_request' })
  }, [amplitudeEvents.next])

  const { formDefinition, steps } = useNewProposalForm({
    allowEmployeePayment: undefined,
    showParticipantsStep,
    defaultEmployeeId: targetEmployeeId,
    currency: company.currency ?? '',
    onSubmit: handleSubmit,
  })

  if (loading) {
    return <Loading />
  }

  return (
    <F0WizardForm
      isOpen={isOpen}
      title={i18n.t('trainings.training_requests.create.training_request_title')}
      formDefinition={formDefinition}
      steps={steps}
      onClose={handleClose}
      onStepChanged={handleStepChanged}
    />
  )
}

export default Wizard
